import { baseApi } from '../../../../store/api/baseApi';

// ========== Query Arg Types ==========

export interface KvsResult9n11QueryArgs {
  year: number;
  term: number;
}

export interface TopPerformers9n11QueryArgs extends KvsResult9n11QueryArgs {
  order?: 'asc' | 'desc';
}

export interface Student9n11DetailQueryArgs {
  year: number;
  term: number;
  studentid: number;
}

export interface Ro9n11QueryArgs {
  year: number;
  term: number;
  roid: number;
}

export interface School9n11DetailQueryArgs {
  year: number;
  term: number;
  kvid: number;
}

// ========== Response Types ==========

export interface KvsResult9n11SummaryClassData {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
}

export interface KvsResult9n11SummaryResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    totalStudents: number;
    avgMarks: number;
    needSupport: number;
    excellentStudents: number;
    byClass: Record<string, KvsResult9n11SummaryClassData>;
  };
  message: string;
}

export interface Subject9n11DistributionItem {
  subjectTitle: string;
  avgGrandTotal: number;
  avgWeightage: number;
  totalStudents: number;
}

export interface KvsResult9n11SubjectwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: Subject9n11DistributionItem[];
    byClass: Record<string, Subject9n11DistributionItem[]>;
  };
  message: string;
}

export interface Performance9n11Distribution {
  excellent: number;
  good: number;
  average: number;
  belowAverage: number;
  total: number;
}

export interface KvsResult9n11DistributionResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: Performance9n11Distribution;
    byClass: Record<string, Performance9n11Distribution>;
  };
  message: string;
}

export interface Region9n11TopBottomSchool {
  schoolID: number;
  schoolName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface Region9n11CompetencyItem {
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
  topSchool: Region9n11TopBottomSchool;
  bottomSchool: Region9n11TopBottomSchool;
}

export interface KvsResult9n11RegionwiseResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    regions: Region9n11CompetencyItem[];
    byClass?: Record<string, Region9n11CompetencyItem[]>;
  };
  message: string;
}

export interface TopPerformer9n11Region {
  rank: number;
  roID: number;
  roName: string;
  avgMarksPercentage: number;
  totalStudents: number;
}

export interface KvsResult9n11TopPerformersResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    order: string;
    regions: TopPerformer9n11Region[];
  };
  message: string;
}

/**
 * Pass/fail counts derived from the `result` column (1 = pass, 2 = fail,
 * 0 = not declared). `passPercentage` is pass / (pass + fail) × 100 and is
 * null — not 0 — when nothing was declared.
 */
export interface PassFailCounts {
  passed: number;
  failed: number;
  notDeclared: number;
  passPercentage: number | null;
}

export interface RoSchool9n11ClassData extends PassFailCounts {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
}

export interface RoSchool9n11Item extends PassFailCounts {
  kvID: number;
  schoolID: number;
  schoolName: string;
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  byClass?: Record<string, RoSchool9n11ClassData | null>;
}

export interface KvsResult9n11RoDetailsResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    roID: number;
    roName: string;
    totalSchools: number;
    schools: RoSchool9n11Item[];
  };
  message: string;
}

export interface PassMetrics9n11 extends PassFailCounts {
  totalStudents: number;
  avgMarks: number;
}

export interface RegionPassAnalyticsRow extends PassMetrics9n11 {
  roID: number;
  roName: string | null;
  byClass: Record<string, PassMetrics9n11 | null>;
}

export interface StreamPassAnalyticsRow extends PassMetrics9n11 {
  stream: number;
  streamName: string;
}

/**
 * Pass-percentage analytics for classes 9 & 11. `streams` compares class-11
 * streams (Science/Commerce/Humanities) — the finest curriculum split the
 * source table supports, since it has no per-subject marks. `coverage` says
 * how many class-11 result rows had a stream assigned.
 */
export interface KvsResult9n11PassAnalyticsResponse {
  success: boolean;
  data: {
    year: number;
    term: string;
    overall: PassMetrics9n11 | null;
    byClass: Record<string, PassMetrics9n11 | null>;
    regions: RegionPassAnalyticsRow[];
    streams: {
      classNum: number;
      coverage: { matched: number; total: number };
      rows: StreamPassAnalyticsRow[];
    };
  };
  message: string;
}

export interface School9n11Student {
  studentID: number;
  studentName: string | null;
  section: string;
  marks: number;
  result: number;
  resultStatus: number;
  workingDay: number;
  presentDay: number;
  rollNo: number | null;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
}

export interface School9n11ClassData {
  totalStudents: number;
  avgMarks: number;
  needSupport: number;
  excellentStudents: number;
  students: School9n11Student[];
}

export interface School9n11DetailData {
  kvID: number;
  schoolID: number;
  schoolName: string;
  roID: number;
  roName: string;
  year: number;
  term: string;
  byClass: Record<string, School9n11ClassData>;
}

export interface KvsResult9n11SchoolDetailResponse {
  success: boolean;
  data: School9n11DetailData;
  message: string;
}

export interface Student9n11Detail {
  studentID: number;
  studentName: string | null;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  section: string;
  marks: number;
  result: number;
  resultStatus: number;
  workingDay: number;
  presentDay: number;
  rollNo: number | null;
  cbseRollNo?: string | null;
  academicSession?: string | null;
  stream?: number | null;
  schoolName?: string;
  class?: string | number;
  [key: string]: any;
}

export interface KvsResult9n11StudentDetailResponse {
  success: boolean;
  data: Student9n11Detail | Student9n11Detail[];
  message?: string;
}

// ========== RTK Query Endpoints ==========

export const kvsResult9n11Api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getKvsResult9n11Summary: build.query<KvsResult9n11SummaryResponse, KvsResult9n11QueryArgs>({
      query: ({ year, term }) => `/kvsresult9n11/summary?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11Subjectwise: build.query<KvsResult9n11SubjectwiseResponse, KvsResult9n11QueryArgs>({
      query: ({ year, term }) => `/kvsresult9n11/subjectwise-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11Distribution: build.query<KvsResult9n11DistributionResponse, KvsResult9n11QueryArgs>({
      query: ({ year, term }) => `/kvsresult9n11/9n11-distribution?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11Regionwise: build.query<KvsResult9n11RegionwiseResponse, KvsResult9n11QueryArgs>({
      query: ({ year, term }) => `/kvsresult9n11/regionwise-competency?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11TopPerformers: build.query<KvsResult9n11TopPerformersResponse, TopPerformers9n11QueryArgs>({
      query: ({ year, term, order = 'desc' }) =>
        `/kvsresult9n11/top-performers-regions?term=${term}&year=${year}&order=${order}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11PassAnalytics: build.query<KvsResult9n11PassAnalyticsResponse, KvsResult9n11QueryArgs>({
      query: ({ year, term }) => `/kvsresult9n11/pass-analytics?term=${term}&year=${year}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11RoDetails: build.query<KvsResult9n11RoDetailsResponse, Ro9n11QueryArgs>({
      query: ({ year, term, roid }) =>
        `/kvsresult9n11/9n11-result-rodetails?term=${term}&year=${year}&roid=${roid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11SchoolDetail: build.query<KvsResult9n11SchoolDetailResponse, School9n11DetailQueryArgs>({
      query: ({ year, term, kvid }) =>
        `/kvsresult9n11/9n11-school-detail?term=${term}&year=${year}&kvid=${kvid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
    getKvsResult9n11StudentDetail: build.query<KvsResult9n11StudentDetailResponse, Student9n11DetailQueryArgs>({
      query: ({ year, term, studentid }) =>
        `/kvsresult9n11/9n11-student-detail?term=${term}&year=${year}&studentid=${studentid}`,
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetKvsResult9n11SummaryQuery,
  useGetKvsResult9n11SubjectwiseQuery,
  useGetKvsResult9n11DistributionQuery,
  useGetKvsResult9n11RegionwiseQuery,
  useGetKvsResult9n11TopPerformersQuery,
  useGetKvsResult9n11PassAnalyticsQuery,
  useGetKvsResult9n11RoDetailsQuery,
  useGetKvsResult9n11SchoolDetailQuery,
  useGetKvsResult9n11StudentDetailQuery,
} = kvsResult9n11Api;
